/**
 * Global Auth State Manager
 * Handles token expiration and prevents refresh loops
 */

import { useAuthStore } from '@/stores/authStore';

class AuthStateManager {
  private static instance: AuthStateManager;
  private isRedirecting = false;
  private redirectTimeout: NodeJS.Timeout | null = null;

  private constructor() {}

  public static getInstance(): AuthStateManager {
    if (!AuthStateManager.instance) {
      AuthStateManager.instance = new AuthStateManager();
    }
    return AuthStateManager.instance;
  }

  /**
   * Handle token expiration - clear auth state and redirect to login
   */
  public async handleTokenExpiration(): Promise<void> {
    if (this.isRedirecting) {
      console.log('Already redirecting, skipping duplicate redirect');
      return;
    }

    this.isRedirecting = true;
    console.log('🔄 Handling token expiration - clearing auth state');

    try {
      // Clear auth store state
      const { clearAuth } = useAuthStore.getState();
      clearAuth();

      // Clear localStorage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('refresh_token_expires_at');
      localStorage.removeItem('user_data');

      // Clear any pending redirects
      if (this.redirectTimeout) {
        clearTimeout(this.redirectTimeout);
      }

      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        console.log('🔄 Redirecting to login page');
        
        // Use replace to prevent back button issues
        this.redirectTimeout = setTimeout(() => {
          window.location.replace('/login');
        }, 100);
      }
    } catch (error) {
      console.error('Error handling token expiration:', error);
    } finally {
      // Reset redirecting flag after a delay
      setTimeout(() => {
        this.isRedirecting = false;
      }, 2000);
    }
  }

  /**
   * Check if currently redirecting
   */
  public isCurrentlyRedirecting(): boolean {
    return this.isRedirecting;
  }

  /**
   * Reset redirecting state (for testing or manual reset)
   */
  public resetRedirectingState(): void {
    this.isRedirecting = false;
    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout);
      this.redirectTimeout = null;
    }
  }
}

export const authStateManager = AuthStateManager.getInstance();
